import { Button } from '@hgc/ui';
import type { JSX, MouseEvent } from 'react';

import { useAppStore } from '../runtime/AppStoreContext';
import { useLinkStatus } from '../runtime/hooks';
import { DEFAULT_LINK_CONFIG } from '../runtime/linkConfig';
import { LinkStatusBadge } from './LinkStatusBadge';
import { ThemeControls } from './ThemeControls';
import { VehicleSelector } from './VehicleSelector';
import { AnalyzeIcon, FlyIcon, LinkIcon, LinkOffIcon, PlanIcon, SetupIcon } from './icons';
import { SECTIONS, navigate, sectionHref, type SectionId } from './route';

/**
 * 应用外壳：顶部一条**深色工具栏**，内容区在其下。
 *
 * ## 为什么分区导航从左侧栏搬到了顶栏
 *
 * 左侧栏在四分区的地面站里是错的：它每条都占掉一列固定的横向空间，而这四列像素
 * 在**飞行分区**最需要（地图、姿态、仪表的可用宽度全被它吃掉），却在设置分区完全浪费。
 * 搬到顶栏之后，横向空间全部还给内容，而"我在哪个分区"变成一条与窗口等宽的指示，
 * 隔着半个屏幕也能一眼看出当前在飞行还是在设置 —— 这在现场比"左侧第几项高亮"可靠得多。
 *
 * 导航仍然是**锚点**（`<a href="#/fly">`）而不是按钮，理由与从前一致：
 * 刷新停在原分区、前进后退可用、链接能直接发给别人、键盘可达性由浏览器免费提供。
 * 外层仍然是 `<nav aria-label="分区">`：这是给读屏器与自动化测试的稳定契约。
 *
 * ## 深色工具栏不是装饰
 *
 * 它在深浅两套主题里都是深色（见 `tokens.css` 的 `--chrome` 说明）：把"我在操作什么"
 *（链路、机体、分区）与"我看到的内容"从视觉上分成两层。状态区因此被包在
 * `.hgc-chrome-scope` 里 —— 那一层把语义令牌就地重映射成深色版本，
 * 于是 `Badge` / `Select` / 外观切换这些**完全不知道自己被放到了深色条上**的组件，
 * 依然是对的。这是语义令牌体系真正兑现价值的地方：换皮只改一处。
 */
export function AppToolbar({ active }: { readonly active: SectionId }): JSX.Element {
  return (
    <header
      aria-label="标题栏"
      className="flex h-chrome shrink-0 items-stretch gap-3 border-b border-chrome-line bg-chrome px-2 text-chrome-fg"
    >
      <Brand />
      <SectionNav active={active} />
      <div className="hgc-chrome-scope ml-auto flex shrink-0 items-center gap-3 pr-1">
        <LinkStatusBadge />
        <VehicleSelector />
        <LinkToggleButton />
        <ThemeControls />
      </div>
    </header>
  );
}

/** 品牌区。`HGC` 方块是唯一一处纯几何的标识，不引图片资源 */
function Brand(): JSX.Element {
  return (
    <div className="flex shrink-0 items-center gap-2 pl-1">
      <span
        aria-hidden="true"
        className="grid h-7 w-7 place-items-center rounded-[calc(var(--radius-control)-1px)] bg-chrome-active text-[11px] font-bold tracking-tight text-chrome-active-fg"
      >
        HGC
      </span>
      <span className="hidden text-sm font-semibold tracking-wide lg:block">
        Havorix Ground Control
      </span>
    </div>
  );
}

/**
 * 分区切换。
 *
 * 四个分区用**图标 + 文字**而不是只有图标：图标能让人一眼定位，文字能让人不必记住
 * "我当初给它画的是什么"。只给图标在内部工具里很常见，但它要求每个使用者都先把
 * 四个图标背下来 —— 而地面站是**偶尔才用一次**的软件，背下来的前提不成立。
 */
function SectionNav({ active }: { readonly active: SectionId }): JSX.Element {
  return (
    <nav aria-label="分区" className="flex shrink-0 items-center gap-0.5">
      {SECTIONS.map((section) => {
        const current = section.id === active;
        return (
          <a
            key={section.id}
            href={sectionHref(section.id)}
            title={section.hint}
            aria-current={current ? 'page' : undefined}
            onClick={(event: MouseEvent<HTMLAnchorElement>) => {
              if (!isPlainLeftClick(event)) return;
              event.preventDefault();
              navigate(section.id);
            }}
            className={
              'flex items-center gap-1.5 rounded-control px-2.5 py-1.5 text-sm no-underline transition-colors ' +
              (current
                ? 'bg-chrome-active font-medium text-chrome-active-fg'
                : 'text-chrome-fg-muted hover:bg-chrome-hover hover:text-chrome-fg')
            }
          >
            <SectionIcon id={section.id} />
            {section.label}
          </a>
        );
      })}
    </nav>
  );
}

function SectionIcon({ id }: { readonly id: SectionId }): JSX.Element {
  switch (id) {
    case 'fly':
      return <FlyIcon size={16} />;
    case 'plan':
      return <PlanIcon size={16} />;
    case 'setup':
      return <SetupIcon size={16} />;
    case 'analyze':
      return <AnalyzeIcon size={16} />;
  }
}

/**
 * 连接开关。
 *
 * "启动就连内置模拟机"这件事必须**在界面上看得出来**，也必须能关掉：
 * 一个分不清"我在看真机还是模拟机"的地面站，比一个连不上的地面站危险得多。
 * 链路标识（`模拟机 · 1 架 多旋翼`）就在它左边，两者是一起被看到的。
 */
function LinkToggleButton(): JSX.Element {
  const store = useAppStore();
  const status = useLinkStatus();
  const connected = status.phase === 'connected' || status.phase === 'connecting';

  return (
    <Button
      size="sm"
      variant={connected ? 'secondary' : 'primary'}
      disabled={status.phase === 'connecting'}
      iconLeft={connected ? <LinkOffIcon size={14} /> : <LinkIcon size={14} />}
      onClick={() => {
        if (connected) {
          void store.link.disconnect();
        } else {
          void store.link.connect(DEFAULT_LINK_CONFIG);
        }
      }}
    >
      {connected ? '断开' : status.phase === 'error' ? '重试模拟机' : '连接模拟机'}
    </Button>
  );
}

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}
